import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Package,
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
  History,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultCategories = [
  "Raw Materials",
  "Components",
  "Hardware",
  "Electrical",
  "Consumables",
  "Tools",
  "Packaging",
  "Other",
];

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showLowStock, setShowLowStock] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [itemToAdjust, setItemToAdjust] = useState(null);
  const [adjustment, setAdjustment] = useState({
    adjustment_type: "add",
    quantity: 0,
    reason: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    description: "",
    category: "",
    unit: "pcs",
    current_stock: 0,
    min_threshold: 0,
    max_threshold: "",
    unit_cost: 0,
    supplier_id: "",
    location: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [itemsRes, suppliersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/inventory`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/inventory/categories`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setItems(itemsRes.data);
      setSuppliers(suppliersRes.data);
      
      const fetchedCats = (categoriesRes.data || []).filter((c) => c && c.trim());
      const allCategories = [...new Set([...defaultCategories, ...fetchedCats])];
      setCategories(allCategories);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        current_stock: parseFloat(formData.current_stock) || 0,
        min_threshold: parseFloat(formData.min_threshold) || 0,
        max_threshold: formData.max_threshold ? parseFloat(formData.max_threshold) : null,
        unit_cost: parseFloat(formData.unit_cost) || 0,
        supplier_id: formData.supplier_id || null,
      };

      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.item_id}`, payload, {
          withCredentials: true,
        });
        toast.success("Inventory item updated");
      } else {
        await axios.post(`${API}/inventory`, payload, { withCredentials: true });
        toast.success("Inventory item created");
      }
      setSheetOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("Failed to save inventory item");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      sku: item.sku || "",
      description: item.description || "",
      category: item.category || "",
      unit: item.unit || "pcs",
      current_stock: item.current_stock || 0,
      min_threshold: item.min_threshold || 0,
      max_threshold: item.max_threshold || "",
      unit_cost: item.unit_cost || 0,
      supplier_id: item.supplier_id || "",
      location: item.location || "",
      notes: item.notes || "",
    });
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${API}/inventory/${itemToDelete.item_id}`, {
        withCredentials: true,
      });
      toast.success("Inventory item deleted");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete inventory item");
    }
  };

  const handleAdjustStock = async () => {
    if (!itemToAdjust) return;
    try {
      await axios.post(
        `${API}/inventory/${itemToAdjust.item_id}/adjust`,
        adjustment,
        { withCredentials: true }
      );
      toast.success("Stock adjusted successfully");
      setAdjustDialogOpen(false);
      setItemToAdjust(null);
      setAdjustment({ adjustment_type: "add", quantity: 0, reason: "" });
      fetchData();
    } catch (error) {
      console.error("Error adjusting stock:", error);
      toast.error("Failed to adjust stock");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      sku: "",
      description: "",
      category: "",
      unit: "pcs",
      current_stock: 0,
      min_threshold: 0,
      max_threshold: "",
      unit_cost: 0,
      supplier_id: "",
      location: "",
      notes: "",
    });
  };

  const isLowStock = (item) => item.current_stock <= item.min_threshold;

  const getStockStatus = (item) => {
    if (item.current_stock <= 0) {
      return { label: "Out of Stock", color: "bg-red-50 text-red-700" };
    }
    if (item.current_stock <= item.min_threshold) {
      return { label: "Low Stock", color: "bg-amber-50 text-amber-700" };
    }
    if (item.max_threshold && item.current_stock >= item.max_threshold) {
      return { label: "Overstocked", color: "bg-blue-50 text-blue-700" };
    }
    return { label: "In Stock", color: "bg-emerald-50 text-emerald-700" };
  };

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.sku && item.sku.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    const matchesLowStock = !showLowStock || isLowStock(item);
    return matchesSearch && matchesCategory && matchesLowStock;
  });

  const lowStockCount = items.filter(isLowStock).length;

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Inventory
          </h1>
          <p className="text-slate-500 mt-1">Manage stock levels and thresholds</p>
        </div>
        <Button
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          onClick={() => setSheetOpen(true)}
          data-testid="add-inventory-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Low Stock Alert */}
      {lowStockCount > 0 && (
        <Card className="rounded-sm border-amber-200 bg-amber-50 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">
                {lowStockCount} item{lowStockCount > 1 ? "s" : ""} below minimum threshold
              </p>
              <p className="text-sm text-amber-600">
                Review low stock items and create purchase orders
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm border-amber-400 text-amber-700 hover:bg-amber-100"
              onClick={() => setShowLowStock(!showLowStock)}
              data-testid="toggle-low-stock-btn"
            >
              {showLowStock ? "Show All" : "View Low Stock"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-inventory"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full md:w-48 rounded-sm">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Inventory Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {filteredItems.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead className="text-right">Min</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => {
                  const status = getStockStatus(item);
                  return (
                    <TableRow 
                      key={item.item_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleEdit(item)}
                      data-testid={`inventory-row-${item.item_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                              isLowStock(item) ? "bg-amber-100" : "bg-slate-100"
                            }`}
                          >
                            <Package
                              className={`h-5 w-5 ${
                                isLowStock(item) ? "text-amber-600" : "text-slate-600"
                              }`}
                            />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{item.name}</p>
                            {item.supplier_id && (
                              <p className="text-sm text-slate-500">
                                {suppliers.find((s) => s.supplier_id === item.supplier_id)?.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 font-mono text-sm">
                        {item.sku || "-"}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {item.category || "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.current_stock} {item.unit}
                      </TableCell>
                      <TableCell className="text-right text-slate-500">
                        {item.min_threshold} {item.unit}
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-sm ${status.color}`}>
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        R {(item.unit_cost || 0).toLocaleString()}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setItemToAdjust(item);
                                setAdjustDialogOpen(true);
                              }}
                            >
                              <ArrowUpCircle className="mr-2 h-4 w-4" />
                              Adjust Stock
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEdit(item)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setItemToDelete(item);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No inventory items found
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery || categoryFilter !== "all" || showLowStock
                  ? "Try adjusting your filters"
                  : "Get started by adding your first inventory item"}
              </p>
              {!searchQuery && categoryFilter === "all" && !showLowStock && (
                <Button
                  onClick={() => setSheetOpen(true)}
                  className="rounded-sm bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingItem ? "Edit Item" : "Add Inventory Item"}
            </SheetTitle>
            <SheetDescription>
              {editingItem
                ? "Update inventory item details"
                : "Add a new item to your inventory"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Item Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Item name"
                  required
                  className="rounded-sm"
                  data-testid="inventory-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="SKU-001"
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Item description"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category || "none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, category: value === "none" ? "" : value })
                  }
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="pcs, kg, m"
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current_stock">Current Stock</Label>
                <Input
                  id="current_stock"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.current_stock}
                  onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_threshold">Min Threshold</Label>
                <Input
                  id="min_threshold"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.min_threshold}
                  onChange={(e) => setFormData({ ...formData, min_threshold: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_threshold">Max Threshold</Label>
                <Input
                  id="max_threshold"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.max_threshold}
                  onChange={(e) => setFormData({ ...formData, max_threshold: e.target.value })}
                  placeholder="Optional"
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit_cost">Unit Cost (R)</Label>
                <Input
                  id="unit_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Preferred Supplier</Label>
                <Select
                  value={formData.supplier_id || "none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, supplier_id: value === "none" ? "" : value })
                  }
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No supplier</SelectItem>
                    {suppliers.map((sup) => (
                      <SelectItem key={sup.supplier_id} value={sup.supplier_id}>
                        {sup.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Storage Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Warehouse, shelf, etc."
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSheetOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="save-inventory-btn"
              >
                {editingItem ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Adjust Stock Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Stock - {itemToAdjust?.name}</DialogTitle>
            <DialogDescription>
              Current stock: {itemToAdjust?.current_stock} {itemToAdjust?.unit}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Adjustment Type</Label>
              <Select
                value={adjustment.adjustment_type}
                onValueChange={(value) =>
                  setAdjustment({ ...adjustment, adjustment_type: value })
                }
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <span className="flex items-center gap-2">
                      <ArrowUpCircle className="h-4 w-4 text-emerald-600" />
                      Add Stock
                    </span>
                  </SelectItem>
                  <SelectItem value="remove">
                    <span className="flex items-center gap-2">
                      <ArrowDownCircle className="h-4 w-4 text-red-600" />
                      Remove Stock
                    </span>
                  </SelectItem>
                  <SelectItem value="set">
                    <span className="flex items-center gap-2">
                      <History className="h-4 w-4 text-blue-600" />
                      Set Exact Amount
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjust-quantity">Quantity</Label>
              <Input
                id="adjust-quantity"
                type="number"
                min="0"
                step="0.01"
                value={adjustment.quantity}
                onChange={(e) =>
                  setAdjustment({ ...adjustment, quantity: parseFloat(e.target.value) || 0 })
                }
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjust-reason">Reason (optional)</Label>
              <Textarea
                id="adjust-reason"
                value={adjustment.reason}
                onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                placeholder="e.g., Physical count, returned stock, damaged"
                rows={2}
                className="rounded-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAdjustDialogOpen(false);
                setItemToAdjust(null);
                setAdjustment({ adjustment_type: "add", quantity: 0, reason: "" });
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdjustStock}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="confirm-adjust-btn"
            >
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inventory Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{itemToDelete?.name}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Inventory;
